import {
  AlertTriangle,
  HandCoins,
  ShieldCheck,
  FileSignature,
  LinkIcon,
  Building2,
  Landmark,
  Wrench,
  Fingerprint,
  Store as StoreIcon,
  Phone as PhoneIcon,
  Car as CarIcon,
  Home as HomeIcon,
  Hash as HashIcon,
  BadgeCheck,
} from 'lucide-react';

/**
 * SafetyTips — critical section shown just above ContactActions on every
 * listing detail page (plates / real-estate / cars / mobile-numbers).
 * Content varies by category. Tone: warning, but well-styled and premium.
 */

const TIPS = {
  plates: {
    ar: [
      { icon: HandCoins,     title: 'الدفع والاستلام وجهاً لوجه', body: 'لا تحوّل أي مبالغ مالية مسبقاً قبل معاينة ومقابلة البائع شخصياً.' },
      { icon: FileSignature, title: 'التعاملات الرسمية والعقود المعتمدة', body: 'احرص على أن تتم كافة العقود والاتفاقيات المالية من خلال الجهات والمؤسسات الرسمية المعتمدة في الدولة.' },
      { icon: Fingerprint,   title: 'التحقق من الهوية', body: 'تأكد دائماً من مطابقة هوية مالك اللوحة مع الأوراق الرسمية الصادرة من المرور.' },
      { icon: LinkIcon,      title: 'الروابط الخارجية', body: 'لا تضغط على أي روابط غريبة أو صفحات دفع وهمية لتحويل ثمن اللوحة.' },
      { icon: Landmark,      title: 'النقل المروري الرسمي', body: 'يجب أن تتم عملية التنازل ونقل ملكية اللوحة حصراً عبر قنوات إدارات المرور والترخيص في الدولة.' },
      { icon: ShieldCheck,   title: 'تجنّب التحويل المسبق', body: 'لا تقم بتحويل أي مبالغ قبل الحضور الشخصي لمراكز الترخيص وإتمام المعاملة رسمياً.' },
    ],
    en: [
      { icon: HandCoins,     title: 'Meet & pay in person',        body: 'Never wire money before meeting the seller and inspecting the plate in person.' },
      { icon: FileSignature, title: 'Official & certified contracts', body: 'Handle all financial agreements through official UAE-recognized authorities and institutions only.' },
      { icon: Fingerprint,   title: 'Verify identity',            body: 'Always match the seller\'s ID with the official plate ownership documents issued by traffic authorities.' },
      { icon: LinkIcon,      title: 'External links',             body: 'Do not click on suspicious links or fake payment pages to transfer the plate cost.' },
      { icon: Landmark,      title: 'Official traffic transfer',  body: 'Plate ownership transfer must be executed exclusively through UAE traffic and licensing departments.' },
      { icon: ShieldCheck,   title: 'Avoid advance payments',     body: 'Do not transfer any funds before physically visiting licensing centers and completing the transaction officially.' },
    ],
    color: '#F26522',
    icon: HashIcon,
  },
  real_estate: {
    ar: [
      { icon: HandCoins,     title: 'الدفع والاستلام وجهاً لوجه', body: 'لا تحوّل أي مبالغ مالية أو عربون إيجار/شراء عبر الإنترنت قبل المعاينة الفعلية للعقار.' },
      { icon: FileSignature, title: 'التعاملات الرسمية والعقود المعتمدة', body: 'احرص على أن تتم كافة العقود والاتفاقيات والمدفوعات من خلال الجهات والمؤسسات الرسمية المعتمدة في الدولة حصراً.' },
      { icon: Fingerprint,   title: 'التحقق من الهوية', body: 'تأكد دائماً من هوية المؤجر أو البائع ومطابقتها لسند الملكية.' },
      { icon: LinkIcon,      title: 'الروابط الخارجية', body: 'لا تضغط على أي روابط أو مواقع وهمية لحجز العقار أو دفع الدفعات.' },
      { icon: BadgeCheck,    title: 'الوسيط المرخّص', body: 'تعامل فقط مع مكاتب ووسطاء عقاريين مرخّصين رسمياً.' },
      { icon: Building2,     title: 'توثيق العقود', body: 'تأكد من صحة سند الملكية، واحرص على توثيق العقود عبر القنوات الحكومية المختصة (مثل البلدية أو الجهات التنظيمية).' },
    ],
    en: [
      { icon: HandCoins,     title: 'Meet & pay in person',        body: 'Never wire deposits or online payments before physically inspecting the property.' },
      { icon: FileSignature, title: 'Official & certified contracts', body: 'All contracts, agreements and payments must go exclusively through official UAE-recognized authorities.' },
      { icon: Fingerprint,   title: 'Verify identity',            body: 'Always verify the landlord or seller\'s identity against the title deed.' },
      { icon: LinkIcon,      title: 'External links',             body: 'Do not click on suspicious links or fake sites for reservations or payments.' },
      { icon: BadgeCheck,    title: 'Licensed broker only',       body: 'Deal only with officially licensed real-estate offices and brokers.' },
      { icon: Building2,     title: 'Document your contract',     body: 'Verify the title deed and register contracts through the proper government channels (e.g. municipality / regulators).' },
    ],
    color: '#0F766E',
    icon: HomeIcon,
  },
  cars: {
    ar: [
      { icon: HandCoins,     title: 'الدفع والاستلام وجهاً لوجه', body: 'لا تحوّل أي مبالغ أو عربون مسبقاً عبر الإنترنت قبل معاينة السيارة على أرض الواقع.' },
      { icon: FileSignature, title: 'التعاملات الرسمية والعقود المعتمدة', body: 'احرص على أن تتم كافة العقود والاتفاقيات المالية وقانون البيع من خلال الجهات والمؤسسات الرسمية المعتمدة في الدولة حصراً.' },
      { icon: Fingerprint,   title: 'التحقق من الهوية', body: 'تأكد دائماً من مطابقة هوية البائع والأوراق الرسمية ورخصة المركبة.' },
      { icon: LinkIcon,      title: 'الروابط الخارجية', body: 'لا تضغط على أي روابط أو صفحات دفع وهمية يرسلها البائع بحجة الحجز.' },
      { icon: Wrench,        title: 'الفحص الفنّي', body: 'افحص السيارة في مراكز الفحص المعتمدة بالدولة حصراً قبل إتمام الشراء.' },
      { icon: ShieldCheck,   title: 'مطابقة رقم الهيكل', body: 'تأكد من تطابق رقم الهيكل (Chassis Number) والبيانات مع أوراق السيارة.' },
    ],
    en: [
      { icon: HandCoins,     title: 'Meet & pay in person',        body: 'Never wire money or deposit online before physically inspecting the car.' },
      { icon: FileSignature, title: 'Official & certified contracts', body: 'All contracts, agreements and sale registration must go exclusively through official UAE-recognized authorities.' },
      { icon: Fingerprint,   title: 'Verify identity',            body: 'Always match the seller\'s ID with the vehicle\'s official ownership documents.' },
      { icon: LinkIcon,      title: 'External links',             body: 'Do not click on any suspicious links or fake payment pages sent by the seller for "reservation".' },
      { icon: Wrench,        title: 'Technical inspection',       body: 'Inspect the car only at UAE-approved inspection centers before completing the purchase.' },
      { icon: ShieldCheck,   title: 'Match chassis number',       body: 'Verify that the Chassis Number and vehicle data match the official ownership papers.' },
    ],
    color: '#1E9BD9',
    icon: CarIcon,
  },
  phone_numbers: {
    ar: [
      { icon: HandCoins,     title: 'الدفع والاستلام وجهاً لوجه', body: 'لا تحوّل أي مبالغ أو عربون مسبقاً قبل التأكد من الرقم ومعاينة تفاصيله.' },
      { icon: FileSignature, title: 'التعاملات الرسمية والعقود المعتمدة', body: 'احرص على أن تتم كافة الاتفاقيات وعمليات نقل الملكية من خلال الجهات والمؤسسات الرسمية المعتمدة في الدولة حصراً.' },
      { icon: Fingerprint,   title: 'التحقق من الهوية', body: 'تأكد من هوية مالك الرقم المسجّل لدى شركة الاتصالات قبل بدء الإجراءات.' },
      { icon: LinkIcon,      title: 'الروابط الخارجية', body: 'لا تضغط على أي روابط أو رسائل وهمية تدّعي إمكانية توثيق الرقم.' },
      { icon: StoreIcon,     title: 'الفرع الرسمي', body: 'يجب أن تتم عملية تنازل ونقل ملكية الرقم مباشرة داخل فروع مزوّد الخدمة المعتمد في الدولة.' },
      { icon: AlertTriangle, title: 'الوعود عن بعد', body: 'احذر من الأقوال التي تدّعي إمكانية نقل الرقم عن بعد أو عبر روابط إلكترونية.' },
    ],
    en: [
      { icon: HandCoins,     title: 'Meet & pay in person',        body: 'Never wire money or deposits before verifying the number and its details.' },
      { icon: FileSignature, title: 'Official & certified contracts', body: 'All agreements and ownership transfer must go exclusively through official UAE-recognized authorities.' },
      { icon: Fingerprint,   title: 'Verify identity',            body: 'Confirm the identity of the number\'s registered owner at the telecom operator before starting the process.' },
      { icon: LinkIcon,      title: 'External links',             body: 'Do not click on suspicious links or messages claiming to "verify" the number.' },
      { icon: StoreIcon,     title: 'Official branch only',       body: 'Number ownership transfer must happen inside an authorized operator branch in the UAE.' },
      { icon: AlertTriangle, title: 'Remote promises',            body: 'Beware of anyone claiming they can transfer the number remotely or via online links.' },
    ],
    color: '#E4002B',
    icon: PhoneIcon,
  },
};

export default function SafetyTips({ category, isAr, isDarkMode }) {
  const meta = TIPS[category];
  if (!meta) return null;
  const items = isAr ? meta.ar : meta.en;
  const HeadIcon = meta.icon;

  return (
    <div
      className={`rounded-2xl overflow-hidden ${
        isDarkMode
          ? 'bg-[#111] border border-white/10'
          : 'bg-white border border-slate-200 shadow-sm'
      }`}
      data-testid={`safety-tips-${category}`}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      {/* Header with subtle warning stripe */}
      <div
        className="flex items-center gap-3 px-5 py-4 border-b"
        style={{
          background: `${meta.color}12`,
          borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)',
        }}
      >
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${meta.color}22`, color: meta.color }}
        >
          <AlertTriangle className="h-5 w-5" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <h3
            className={`text-sm sm:text-base font-black leading-tight ${
              isDarkMode ? 'text-white' : 'text-slate-900'
            }`}
          >
            {isAr ? 'نصائح للتعامل الآمن' : 'Safe transaction tips'}
          </h3>
          <p
            className={`text-[11px] sm:text-xs mt-0.5 ${
              isDarkMode ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            <HeadIcon className="inline h-3 w-3 me-1 -mt-0.5" />
            {isAr ? 'اقرأ هذه النقاط قبل التواصل مع البائع' : 'Please read these before contacting the seller'}
          </p>
        </div>
      </div>

      {/* Tips grid */}
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px" style={{
        background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)',
      }}>
        {items.map((t, i) => {
          const Icon = t.icon;
          return (
            <li
              key={i}
              className={`flex items-start gap-3 p-4 ${
                isDarkMode ? 'bg-[#111]' : 'bg-white'
              }`}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                style={{ background: `${meta.color}18`, color: meta.color }}
              >
                <Icon className="h-4 w-4" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <h4
                  className={`text-sm font-bold mb-1 ${
                    isDarkMode ? 'text-white' : 'text-slate-900'
                  }`}
                >
                  {t.title}
                </h4>
                <p
                  className={`text-xs leading-relaxed ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-600'
                  }`}
                >
                  {t.body}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
